// Single source of truth for every themeable color: the CSS custom property
// it drives, the localStorage/state key, which group it's shown under in the
// Settings color editor, whether it's a raw-text (gradient) value rather than
// a flat color, and its light/dark default. Layout.jsx, App.js, and
// ThemeEditorPanel.jsx all read from this list instead of hardcoding values.
//
// The 11 entries marked "existing" are exactly what Layout.jsx's
// handlerTheme() hardcoded before this refactor (values carried over
// unchanged, except bgModalColor's alpha - the old code set 0.478 on the
// live state but 0.88 in localStorage, a copy-paste drift bug; unified here
// on 0.478, the value that was actually visible after a toggle).
// The 9 entries marked "new" fix a real bug: App.css's :root defines these
// as colors but Layout.jsx's toggle never touched them, so light mode was
// silently stuck on the dark value for all of them. Light defaults for pure
// glow/accent tokens are kept identical to dark (inverting them would be
// arbitrary) - all of this becomes user-tunable via the Settings panel
// anyway, so these are just sane starting points, not load-bearing.
// `preview` selects which mini live-mockup ColorPreview.jsx renders next to
// the control - a small set of reusable archetypes (surface/text/button/...)
// rather than 20 bespoke ones, each one pulling in sibling colors from the
// active palette too (e.g. the button preview's label uses mainTextColor),
// so the preview reflects this color "in symbiosis" with the rest of the
// current configuration, not in isolation.
export const THEME_COLOR_KEYS = [
  // backgrounds
  { cssVar: '--bg-color', key: 'bgColor', group: 'backgrounds', rawText: false, preview: 'surface',
    dark: '#1e2322', light: 'white' },
  { cssVar: '--bgprimary-color', key: 'bgPrimaryColor', group: 'backgrounds', rawText: true, preview: 'surface',
    dark: 'linear-gradient(333deg, rgb(18 20 22) 1%, rgb(52 97 91) 100%)',
    light: 'linear-gradient(110deg, rgb(229 155 255) 0%, rgb(132 222 255 / 98%) 100%)' },
  { cssVar: '--secondary-fill-color', key: 'bgSecondaryFillColor', group: 'backgrounds', rawText: false, preview: 'surface',
    dark: 'rgb(218, 238, 252)', light: 'rgb(79, 131, 168)' },
  { cssVar: '--bg-buttons-color', key: 'bgButtonsColor', group: 'backgrounds', rawText: true, preview: 'button',
    dark: 'radial-gradient(circle, rgb(0 0 0 / 98%) 0%, rgb(54 75 77) 100%)',
    light: 'radial-gradient(circle, rgb(255 255 255) 0%, rgb(210 247 255) 80%)' },
  { cssVar: '--bg-modal-color', key: 'bgModalColor', group: 'backgrounds', rawText: false, preview: 'overlay',
    dark: 'rgba(14, 14, 14, 0.478)', light: 'rgba(255, 255, 255, 0.97)' },
  { cssVar: '--bg-modal-color2', key: 'bgModalColor2', group: 'backgrounds', rawText: true, preview: 'panel',
    dark: 'linear-gradient(45deg, rgba(2, 29, 27, 1), rgba(1, 9, 26, 1))',
    light: 'linear-gradient(45deg, rgb(227 255 253 / 100%), rgb(223 233 255 / 100%))' },
  { cssVar: '--theme-bgGreef', key: 'bgGreef', group: 'backgrounds', rawText: false, preview: 'panel',
    dark: 'rgb(21 43 31 / 23%)', light: 'rgb(234 249 255 / 53%)' },
  { cssVar: '--bg-table-color', key: 'bgTableColor', group: 'backgrounds', rawText: false, preview: 'table-row',
    dark: 'rgb(10 20 18 / 52%)', light: 'rgb(235 245 251 / 48%)' },
  { cssVar: '--bg-inputs-color', key: 'bgInputsColor', group: 'backgrounds', rawText: false, preview: 'input',
    dark: 'rgb(59 63 69 / 34%)', light: 'rgb(255 255 255)' },
  { cssVar: '--bg-infopanels', key: 'bgInfopanels', group: 'backgrounds', rawText: true, preview: 'panel', // new
    dark: 'linear-gradient(333deg, rgba(28,43,255,.086) 1%, rgba(4,255,196,.12) 100%)',
    light: 'linear-gradient(333deg, rgba(28,43,255,.086) 1%, rgba(4,255,196,.12) 100%)' },
  { cssVar: '--bg-element-color', key: 'bgElementColor', group: 'backgrounds', rawText: false, preview: 'highlight', // new
    dark: 'rgba(0, 132, 255, 0.041)', light: 'rgba(0, 132, 255, 0.041)' },

  // text
  { cssVar: '--maintext-color', key: 'mainTextColor', group: 'text', rawText: false, preview: 'text',
    dark: '#f3f3f3', light: '#2f343d' },
  { cssVar: '--secondarytext-color', key: 'secondaryTextColor', group: 'text', rawText: false, preview: 'text',
    dark: '#f3f3f3f0', light: '#2f343df0' },
  { cssVar: '--lighttext-color', key: 'lighttextColor', group: 'text', rawText: false, preview: 'text', // new
    dark: '#63dac8', light: '#63dac8' },

  // borders & lines
  { cssVar: '--border-color', key: 'borderColor', group: 'borders', rawText: false, preview: 'border', // new
    dark: 'hsl(203, 11%, 23%)', light: 'hsl(203, 11%, 82%)' },
  { cssVar: '--border-left-color', key: 'borderLeftColor', group: 'borders', rawText: false, preview: 'border-left', // new
    dark: 'hsl(147, 52%, 68%)', light: 'hsl(147, 52%, 68%)' },
  { cssVar: '--lines-color', key: 'linesColor', group: 'borders', rawText: false, preview: 'divider', // new
    dark: 'hsla(275, 8%, 46%, 0.247)', light: 'hsla(275, 8%, 75%, 0.247)' },

  // accents & shadows
  { cssVar: '--secondayr-btns', key: 'secondayrBtns', group: 'accents', rawText: false, preview: 'button-outline', // new
    dark: '#07268d38', light: 'hsla(230, 90%, 85%, 0.22)' },
  { cssVar: '--shadow-color-one', key: 'shadowColorOne', group: 'accents', rawText: false, preview: 'glow', // new
    dark: '#00ffff10', light: '#00ffff10' },
  { cssVar: '--shadow-color-two', key: 'shadowColorTwo', group: 'accents', rawText: false, preview: 'glow', // new
    dark: '#50e8fc4d', light: '#50e8fc4d' },
];

export const THEME_GROUPS = ['backgrounds', 'text', 'borders', 'accents'];

function localStorageKey(mode, key) {
  return 'theme-' + mode + '-' + key;
}

export function loadPaletteFromLocalStorage(mode) {
  const palette = {};
  for (const entry of THEME_COLOR_KEYS) {
    const stored = localStorage.getItem(localStorageKey(mode, entry.key));
    palette[entry.key] = stored !== null ? stored : entry[mode];
  }
  return palette;
}

export function persistPaletteToLocalStorage(mode, palette) {
  for (const entry of THEME_COLOR_KEYS) {
    if (palette[entry.key] !== undefined) {
      localStorage.setItem(localStorageKey(mode, entry.key), palette[entry.key]);
    }
  }
}

export function seedDefaultThemeLocalStorage() {
  if (localStorage.getItem('theme-dark-bgColor') !== null) return;
  persistPaletteToLocalStorage('light', Object.fromEntries(THEME_COLOR_KEYS.map((e) => [e.key, e.light])));
  persistPaletteToLocalStorage('dark', Object.fromEntries(THEME_COLOR_KEYS.map((e) => [e.key, e.dark])));
}
